<template>
  <BaseDialog
    contentClass="extract-fields"
    ref="dialog"
    :show-actions="true"
    sidePanel
    white-content
    @closeModal="closeDialog"
  >
    <template slot="title">
      <span class="extract-fields__title">
        <BaseIcon name="ai" class="extract-fields__title-icon" />
        {{ $t('page.admin-tenant-profile.extract-dialog.title') }}
      </span>
    </template>
    <template slot="content">
      <div class="extract-fields__body">
        <p class="extract-fields__subtitle">{{ $t('page.admin-tenant-profile.extract-dialog.subtitle') }}</p>

        <div class="extract-fields__section">
          <FormLabel>{{ $t('page.admin-tenant-profile.extract-dialog.documents-label') }}</FormLabel>
          <FileUpload
            multiple
            display-horizontal
            :is-loading="false"
            :disabled="extracting || files.length >= maxFiles"
            :allowed-files="acceptedFiles"
            @add-files="onFilesAdded"
          />
          <p class="extract-fields__hint">
            {{ $t('page.admin-tenant-profile.extract-dialog.max-files-hint', { count: maxFiles }) }}
          </p>
          <ul v-if="files.length" class="extract-fields__files">
            <li v-for="(file, index) in files" :key="index" class="extract-fields__file">
              <BaseIcon name="file-text" />
              <span class="extract-fields__file-name">{{ file.name }}</span>
              <BaseRoundButton
                :size="28"
                icon="trash-2"
                is-transparent
                is-red
                :disabled="extracting"
                :tooltip-text="$t('actions.delete')"
                @click.native="removeFile(index)"
              />
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template slot="actions">
      <div class="extract-fields__footer">
        <BaseButton is-outlined @click.native="closeDialog">{{ $t('actions.cancel') }}</BaseButton>
        <BaseButton
          is-ai
          has-icon-left
          :disabled="extracting || !files.length"
          :is-loading="extracting"
          @click.native="onExtract"
        >
          <BaseIcon name="ai" />
          {{
            extracting
              ? $t('page.admin-tenant-profile.extract-dialog.extracting')
              : files.length
              ? $tc('page.admin-tenant-profile.extract-dialog.cta', files.length, { count: files.length })
              : $t('page.admin-tenant-profile.extract-dialog.cta-empty')
          }}
        </BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import FormLabel from '@/components/form/FormLabel.vue';
import FileUpload from '@/components/form/FileUpload.vue';
import { mapActions } from 'vuex';

const MAX_FILES = 3;

export default {
  name: 'ExtractFieldsDialog',
  components: { BaseButton, BaseDialog, BaseIcon, BaseRoundButton, FormLabel, FileUpload },
  data() {
    return {
      files: [],
      extracting: false,
      acceptedFiles: '.pdf,.doc,.docx,.xls,.xlsx',
      maxFiles: MAX_FILES,
    };
  },
  beforeDestroy() {
    this.cancelExtraction();
  },
  methods: {
    ...mapActions('tenantProfile', ['extractFromDocuments', 'cancelExtraction']),
    openDialog() {
      this.files = [];
      this.extracting = false;
      this.$refs.dialog.openModal();
    },
    closeDialog() {
      if (this.extracting) return;
      this.$refs.dialog.isModalOpen = false;
    },
    onFilesAdded(added) {
      const incoming = Array.from(added);
      // Warn (rather than silently drop) when the selection would exceed the cap.
      if (this.files.length + incoming.length > MAX_FILES) {
        this.$toast.warning(
          this.$t('page.admin-tenant-profile.extract-dialog.max-files-warning', { count: MAX_FILES }),
        );
      }
      this.files = [...this.files, ...incoming].slice(0, MAX_FILES);
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    onExtract() {
      if (!this.files.length) return;
      this.extracting = true;
      this.extractFromDocuments({ files: this.files }).then((result) => {
        this.extracting = false;
        if (!result) return; // import failed/cancelled — keep the dialog open
        this.$emit('done', result);
        this.$refs.dialog.isModalOpen = false;
      });
    },
  },
};
</script>

<style lang="scss">
.base-dialog__extract-fields {
  width: 500px;
}

.extract-fields {
  &__title {
    display: inline-flex;
    align-items: center;
    gap: $space-sm;
  }

  &__title-icon {
    color: $color-semantic-ai;
    width: 22px;
    height: 22px;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: $space-lg;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: $space-sm;
  }

  &__subtitle {
    @include text-style('body-md');
    color: $color-text-body;
    margin: 0;
  }

  &__hint {
    @include text-style('body-sm');
    color: $color-text-muted;
    margin: 0;
  }

  &__files {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: $space-2;
  }

  &__file {
    display: flex;
    align-items: center;
    gap: $space-sm;
    @include text-style('body-sm');
    color: $color-text-body;

    .base-icon {
      width: 16px;
      height: 16px;
      color: $color-text-muted;
    }
  }

  &__file-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: $space-sm;
    padding: $space-lg $space-xl;
  }
}
</style>
